import { FeatureFlagsDto } from '@cibli/shared';

import api from './api';

/** Check a VITE_FLAG_* env var (string "true"/"false"). */
const envFlag = (key: string): boolean | undefined => {
  const val = import.meta.env[key];
  if (val === 'true') return true;
  if (val === 'false') return false;
  return undefined;
};

/** Apply env var overrides to a flag — env takes precedence over DB. */
const withEnvOverride = (
  flag: FeatureFlagsDto[keyof FeatureFlagsDto] | undefined,
  envKey: string,
): FeatureFlagsDto[keyof FeatureFlagsDto] => {
  const base = flag ?? { enabled: false, description: null };
  const override = envFlag(envKey);
  return override !== undefined ? { ...base, enabled: override } : base;
};

export const featureFlagsService = {
  getFeatureFlags: async (boothId?: string): Promise<FeatureFlagsDto> => {
    const params = boothId ? { params: { boothId } } : undefined;
    return api.get<FeatureFlagsDto>('/feature-flags', params);
  },
  isDevNoInterviewActivated: (flags?: FeatureFlagsDto | null): boolean => {
    const f = withEnvOverride(flags?.devNoInterview, 'VITE_FLAG_DEV_NO_INTERVIEW');
    return Boolean(f.enabled);
  },
  isDevNoTimeoutActivated: (flags?: FeatureFlagsDto | null): boolean => {
    const f = withEnvOverride(flags?.devNoTimeout, 'VITE_FLAG_DEV_NO_TIMEOUT');
    return Boolean(f.enabled);
  },
  isSimulationModeActivated: (flags?: FeatureFlagsDto | null): boolean => {
    const f = withEnvOverride(flags?.enableSimulationMode, 'VITE_DEV_SIMULATION_MODE');
    return Boolean(f.enabled);
  },
  isKioskDashboardButtonActivated: (flags?: FeatureFlagsDto | null): boolean => {
    const f = withEnvOverride(
      flags?.enableKioskDashboardButton,
      'VITE_FLAG_ENABLE_KIOSK_DASHBOARD_BUTTON',
    );
    return Boolean(f.enabled);
  },
  isDevOtpBypassActivated: (flags?: FeatureFlagsDto | null): boolean => {
    const f = withEnvOverride(flags?.enableDevOtpBypass, 'VITE_FLAG_ENABLE_DEV_OTP_BYPASS');
    return Boolean(f.enabled);
  },
  isFranceTravailConnectActivated: (flags?: FeatureFlagsDto | null): boolean => {
    const f = withEnvOverride(
      flags?.enableFranceTravailConnect,
      'VITE_FLAG_ENABLE_FRANCE_TRAVAIL_CONNECT',
    );
    return Boolean(f.enabled);
  },
  getDevNoInterviewFlag: async (): Promise<boolean> => {
    const flags = await featureFlagsService.getFeatureFlags();
    return featureFlagsService.isDevNoInterviewActivated(flags);
  },
  getFranceTravailConnectFlag: async (): Promise<boolean> => {
    const flags = await featureFlagsService.getFeatureFlags();
    return featureFlagsService.isFranceTravailConnectActivated(flags);
  },
  getDevNoTimeoutFlag: async (): Promise<boolean> => {
    const flags = await featureFlagsService.getFeatureFlags();
    return featureFlagsService.isDevNoTimeoutActivated(flags);
  },
  getSimulationModeFlag: async (): Promise<boolean> => {
    const flags = await featureFlagsService.getFeatureFlags();
    return featureFlagsService.isSimulationModeActivated(flags);
  },
  isMaintenanceModeActivated: (flags?: FeatureFlagsDto | null): boolean => {
    const f = withEnvOverride(flags?.enableMaintenanceMode, 'VITE_FLAG_ENABLE_MAINTENANCE_MODE');
    return Boolean(f.enabled);
  },
  isSecretPinActivated: (flags?: FeatureFlagsDto | null): boolean => {
    const f = withEnvOverride(
      flags?.enableSecretPin ?? { enabled: true, description: null },
      'VITE_FLAG_ENABLE_SECRET_PIN',
    );
    return Boolean(f.enabled);
  },
  isCvThemesActivated: (flags?: FeatureFlagsDto | null): boolean => {
    const f = withEnvOverride(flags?.cvThemes, 'VITE_FLAG_CV_THEMES');
    return Boolean(f.enabled);
  },
  isBoothDefaultLocaleActivated: (flags?: FeatureFlagsDto | null): boolean => {
    const f = withEnvOverride(
      flags?.enableBoothDefaultLocale,
      'VITE_FLAG_ENABLE_BOOTH_DEFAULT_LOCALE',
    );
    return Boolean(f.enabled);
  },
};
